import { History } from 'history';
import * as React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { Router } from 'react-router';
import { Route, RouteComponentProps } from 'react-router-dom';
import { Store } from 'redux';
import { BasedataPage } from 'src/modules/basedata/pages/BasedataPage';
import { ConfigProvider } from 'src/modules/config/containers/ConfigProvider';
import { ErrorBoundary } from 'src/modules/error/containers/ErrorBoundary';
import { ErrorPage } from 'src/modules/error/pages/ErrorPage';
import { EvaluationPage } from 'src/modules/evaluation/pages/EvaluationPage';
import { HvacDistributionPage } from 'src/modules/hvac/pages/HvacDistributionPage';
import { HvacEmissionPage } from 'src/modules/hvac/pages/HvacEmissionPage';
import { HvacGenerationPage } from 'src/modules/hvac/pages/HvacGenerationPage';
import { HvacResultsPage } from 'src/modules/hvac/pages/HvacResultsPage';
import { HvacSettingsPage } from 'src/modules/hvac/pages/HvacSettingsPage';
import { LoginPage } from 'src/modules/login/pages/LoginPage';
import { ModalStack } from 'src/modules/modals/pages/ModalStack';
import { OrganizationPage } from 'src/modules/organization/pages/OrganizationPage';
import { PartnerDashboardPage } from 'src/modules/partner-dashboard/pages/PartnerDashboardPage';
import { PasswordForgotPage } from 'src/modules/password-forgot/pages/PasswordForgotPage';
import { PasswordResetPage } from 'src/modules/password-reset/pages/PasswordResetPage';
import { PasswordSetupPage } from 'src/modules/password-reset/pages/PasswordSetupPage';
import { ProfilePage } from 'src/modules/profile/page/ProfilePage';
import { ProjectListPage } from 'src/modules/project-list/pages/ProjectListPage';
import { ReportDocumentPage } from 'src/modules/report/pages/ReportDocumentPage';
import { ReportPage } from 'src/modules/report/pages/ReportPage';
import { Initialization } from 'src/modules/routing/containers/Initialization';
import { RouteSwitch } from 'src/modules/routing/containers/RouteSwitch';
import { RouteNotFoundError } from 'src/modules/routing/errors/RouteNotFoundError';
import { ScreenSizeGuard } from 'src/modules/screen-size-guard/views/ScreenSizeGuard';
import { ScrollRestoration } from 'src/modules/scroll/containers/ScrollRestoration';
import { UsersPage } from 'src/modules/users/pages/UsersPage';
import { RuntimeProvider } from 'src/runtimeContext';
import { RootState } from 'src/types/RootState';

type Props = {
  readonly store: Store<RootState>;
  readonly history: History;
  readonly modalRoot: HTMLElement;
};

export const Root = ({ store, history, modalRoot }: Props): React.ReactElement => (
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <ErrorBoundary>
          <RuntimeProvider modalRoot={modalRoot}>
            <ConfigProvider>
              <ScreenSizeGuard>
                <Router history={history}>
                  <Initialization>
                    <>
                      <ScrollRestoration history={history}/>

                      <RouteSwitch>
                        <Route
                          path="/login"
                          exact={true}
                          component={LoginPage}
                        />
                        <Route
                          path="/password/forgot"
                          exact={true}
                          component={PasswordForgotPage}
                        />
                        <Route
                          path="/password/reset"
                          exact={true}
                          component={PasswordResetPage}
                        />
                        <Route
                          path="/password/setup"
                          exact={true}
                          component={PasswordSetupPage}
                        />
                        <Route
                          path="/admin/users"
                          exact={true}
                          component={UsersPage}
                        />
                        <Route
                          path="/admin/organization"
                          exact={true}
                          component={OrganizationPage}
                        />
                        <Route
                          path="/projects"
                          exact={true}
                          component={ProjectListPage}
                        />
                        <Route
                          path="/project/:id([-a-f0-9]+)"
                          exact={true}
                          component={BasedataPage}
                        />
                        <Route
                          path="/project/:id/hvac/settings"
                          exact={true}
                          component={HvacSettingsPage}
                        />
                        <Route
                          path="/project/:id/hvac/emission"
                          exact={true}
                          component={HvacEmissionPage}
                        />
                        <Route
                          path="/project/:id/hvac/distribution"
                          exact={true}
                          component={HvacDistributionPage}
                        />
                        <Route
                          path="/project/:id/hvac/generation"
                          exact={true}
                          component={HvacGenerationPage}
                        />
                        <Route
                          path="/project/:id/hvac/results"
                          exact={true}
                          component={HvacResultsPage}
                        />
                        <Route
                          path="/project/:id/evaluation"
                          exact={true}
                          component={EvaluationPage}
                        />
                        <Route
                          path="/project/:id/report"
                          exact={true}
                          component={ReportPage}
                        />
                        <Route
                          path="/project/:id/report/document"
                          exact={true}
                          component={ReportDocumentPage}
                        />
                        <Route
                          path="/profile"
                          exact={true}
                          component={ProfilePage}
                        />
                        <Route
                          path="/partner-dashboard"
                          exact={true}
                          component={PartnerDashboardPage}
                        />
                        <Route
                          path="*"
                          component={NotFound}
                        />
                      </RouteSwitch>
                      <ModalStack/>
                    </>
                  </Initialization>
                </Router>
              </ScreenSizeGuard>
            </ConfigProvider>
          </RuntimeProvider>
        </ErrorBoundary>
      </Provider>
    </HelmetProvider>
  </React.StrictMode>
);

const NotFound = ({ location }: RouteComponentProps): React.ReactElement => {
  const error = new RouteNotFoundError(location);
  return <ErrorPage error={error}/>;
};
